import React from 'react';
import {
  Box,
  Text,
  Stack,
  Group,
  Select,
  Switch,
  LoadingOverlay,
  TextInput,
  MultiSelect,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconExternalLink, IconInfoCircle } from '@tabler/icons';
import { 
  AvailableIntegration, 
  Integration,
  AvailableFilter,
  SelectFilter,
  TextFilter,
  MultiSelectFilter,
  ToggleFilter,
  DateFilter,
  RecordFieldFilter
} from '@bageltechnologies/bagel-common';
import { useIntegrationConfig } from '../../../../hooks/useIntegrations';
import { RecordFieldFilter as RecordFieldFilterComponent } from './RecordFieldFilter';

interface SettingsTabProps {
  integration: {
    available: AvailableIntegration;
    connected?: Integration;
  };
}

// Type guards for filter types
const isSelectFilter = (filter: AvailableFilter): filter is SelectFilter => filter.type === 'select';
const isTextFilter = (filter: AvailableFilter): filter is TextFilter => filter.type === 'text';
const isMultiSelectFilter = (filter: AvailableFilter): filter is MultiSelectFilter => filter.type === 'multiselect';
const isToggleFilter = (filter: AvailableFilter): filter is ToggleFilter => filter.type === 'toggle';
const isDateFilter = (filter: AvailableFilter): filter is DateFilter => filter.type === 'date';
const isRecordFieldFilter = (filter: AvailableFilter): filter is RecordFieldFilter => filter.type === 'recordField';

export const SettingsTab: React.FC<SettingsTabProps> = ({ integration }) => {
  const { mutateAsync: updateConfig, isPending: isUpdating } = useIntegrationConfig();
  const { available, connected } = integration;

  // Get the available filters from the integration settings
  const availableFilters = available.settings?.availableFilters || [];
  console.log({settings: available.settings});
  // Get the current sync filters configuration
  const currentFilters = connected?.settings?.syncFilters || {};

  const handleSettingChange = async (fieldPath: string, value: any) => {
    try {
      await updateConfig({
        key: available.key,
        config: {
          settings: {
            ...connected?.settings,
            syncFilters: {
              ...currentFilters,
              [fieldPath]: value
            }
          }
        } as unknown as Partial<Omit<Integration, 'accountId'>> & { accountId?: string }
      });
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const renderLabelWithInfo = (filter: AvailableFilter) => {
    if (!filter.info?.url) return filter.label;
    
    return (
      <Group spacing={5}>
        <span>{filter.label}</span>
        <Tooltip w={300} withArrow label={
          <>
            <Text size="xs" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {filter.info.text}
            </Text>
            <Text align='right' mt="6px" size={10}><IconExternalLink size={12} /> Click to open documentation</Text>
          </>}>
          <ActionIcon 
            size="xs" 
            component="a" 
            href={filter.info.url} 
            color="indigo"
            target="_blank"
          >
            <IconInfoCircle size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  };

  const renderSettingField = (filter: AvailableFilter) => {
    const currentValue = currentFilters[filter.fieldPath];

    if (isTextFilter(filter)) {
      return (
        <Box key={filter.id}>
          <TextInput
            label={renderLabelWithInfo(filter)}
            description={filter.description}
            value={currentValue ?? ''}
            onChange={(event) => handleSettingChange(filter.fieldPath, event.currentTarget.value)}
            required={filter.required}
            placeholder={filter.placeholder}
            size="sm"
          />
        </Box>
      );
    }
      
    if (isSelectFilter(filter)) {
      return (
        <Box key={filter.id}>
          <Select
            label={renderLabelWithInfo(filter)}
            description={filter.description}
            value={currentValue?.toString() ?? ''}
            data={filter.options || []}
            onChange={(value) => handleSettingChange(filter.fieldPath, value)}
            required={filter.required}
            creatable={filter.allowNewOptions}
            getCreateLabel={(query) => `+ Create ${query}`}
            size="sm"
          />
        </Box>
      );
    }

    if (isMultiSelectFilter(filter)) {
      return (
        <Box key={filter.id}>
          <MultiSelect
            label={renderLabelWithInfo(filter)}
            description={filter.description}
            value={Array.isArray(currentValue) ? currentValue : []}
            data={[...(filter.options || []), ...(currentValue ?? [])]}
            onChange={(value) => handleSettingChange(filter.fieldPath, value)}
            required={filter.required}
            searchable
            creatable
            getCreateLabel={(query) => `+ Add ${query}`}
            size="sm"
          />
        </Box>
      );
    }

    if (isToggleFilter(filter)) {
      return (
        <Box key={filter.id}>
          <Switch
            label={renderLabelWithInfo(filter)}
            description={filter.description}
            checked={currentValue ?? filter.defaultValue ?? false}
            onChange={(event) => handleSettingChange(filter.fieldPath, event.currentTarget.checked)}
            onLabel={filter.trueLabel || "Yes"}
            offLabel={filter.falseLabel || "No"}
            size="sm"
          />
        </Box>
      );
    }

    if (isDateFilter(filter)) {
      const value = currentValue ?? filter.defaultValue ?? '';
      return (
        <Box key={filter.id}>
          <DateInput
            label={renderLabelWithInfo(filter)}
            description={filter.description}
            value={value ? new Date(value) : null}
            onChange={(date: Date | null) => handleSettingChange(filter.fieldPath, date ? date.toISOString() : null)}
            required={filter.required}
            placeholder="Select a date"
            size="sm"
            clearable
          />
        </Box>
      );
    }

    if (isRecordFieldFilter(filter)) {
      const labelString = filter.label;
      
      return (
        <Box key={filter.id}>
          <RecordFieldFilterComponent
            id={filter.id}
            label={labelString}
            description={filter.description}
            fieldPath={filter.fieldPath}
            required={filter.required}
            value={currentValue}
            onChange={(value) => handleSettingChange(filter.fieldPath, value)}
          />
          {filter.info?.url && (
            <Group spacing={5} mt={5}>
              <Tooltip label={filter.info.text || "Open documentation"}>
                <ActionIcon 
                  size="xs" 
                  component="a" 
                  href={filter.info.url} 
                  color="indigo"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <IconInfoCircle size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Box>
      );
    }
      
    return null;
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isUpdating} overlayBlur={2} />
      <Box>
        <Text weight={500} size="md">Integration Settings</Text>
        <Text size="sm" color="dimmed" mb="xl">
            Configure how this integration syncs data with Bagel.
        </Text>
      </Box>

      <Stack spacing="md">
        {availableFilters.map((filter) => (
          <Box key={filter.id}>
            {renderSettingField(filter)}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}; 